import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Fade, Zoom } from 'react-awesome-reveal';
import { Link } from 'react-router-dom';

const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(79, 70, 229, 0); }
  100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0); }
`;

const CtaWrapper = styled.section`
  background: linear-gradient(135deg, #e0e7ff 0%, #ede9fe 50%, #ffffff 100%);
  color: #1a1a1a;
  padding: 80px 20px;
  margin: 80px auto;
  max-width: 1200px;
  border-radius: 24px;
  border: 1px solid #c7d2fe;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(79, 70, 229, 0.08);
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    padding: 50px 15px;
    margin: 30px 15px;
    border-radius: 16px;
  }
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  opacity: 0.3;
  background-image: radial-gradient(#4f46e5 2px, transparent 2px), radial-gradient(#6366f1 2px, #e0e7ff 2px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 40px;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e0e7ff;
  border: 1px solid #c7d2fe;
  color: #4f46e5;
  padding: 6px 16px;
  border-radius: 30px;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: fit-content;

  @media (max-width: 968px) {
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  font-size: 42px;
  font-weight: 900;
  line-height: 1.15;
  color: #111827;
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;

  span {
    background: linear-gradient(135deg, #4f46e5, #6366f1, #3730a3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const Description = styled.p`
  font-size: 17px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  max-width: 600px;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 968px) {
    margin: 0 auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(Link)`
  background: linear-gradient(135deg, #4f46e5, #4338ca);
  color: #ffffff;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 800;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.2s, opacity 0.2s;
  animation: ${pulseGlow} 2.5s infinite;
  word-break: break-word;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.95;
    color: #ffffff;
  }
`;

const SecondaryButton = styled(Link)`
  background: #ffffff;
  border: 1px solid #d1d5db;
  color: #1f2937;
  padding: 16px 28px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s, border-color 0.2s;
  word-break: break-word;

  &:hover {
    background: #f9fafb;
    border-color: #4f46e5;
    color: #1f2937;
  }
`;

const RightIllustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const FloatingCard = styled.div`
  background: #ffffff;
  border: 1px solid #c7d2fe;
  padding: 24px;
  border-radius: 20px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 30px rgba(79, 70, 229, 0.12);
  animation: ${floatAnimation} 4s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  background: #e0e7ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const CardHeading = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CardSub = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;
`;

const StatItem = styled.div`
  background: #faf5ff;
  border: 1px solid #f3f4f6;
  padding: 12px;
  border-radius: 10px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #4f46e5;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  text-transform: uppercase;
  font-weight: 600;
`;

export default function KidsCodeCtaSection() {
  return (
    <CtaWrapper>
      <BackgroundElements />
      <ContentGrid>
        <Fade triggerOnce direction="left">
          <LeftContent>
            <Badge>⚡ Next-Gen Tech Education</Badge>
            <Title>
              Prepare Your Child For The <span>AI & Coding Era</span>
            </Title>
            <Description>
              Don't let your kids just consume technology—teach them how to build it. Enroll them in our elite coding and robotics bootcamp designed to transform curious minds into tech creators.
            </Description>
            <ButtonGroup>
              <PrimaryButton to="/kidscodeandtech">
                Explore & Register 🚀
              </PrimaryButton>
              {/* <SecondaryButton to="/kidscodeandtech">
                View Curriculum
              </SecondaryButton> */}
            </ButtonGroup>
          </LeftContent>
        </Fade>

        <Zoom triggerOnce>
          <RightIllustration>
            <FloatingCard>
              <CardHeaderRow>
                <IconBox>🤖</IconBox>
                <div>
                  <CardHeading>Kids Code & Tech</CardHeading>
                  <CardSub>Practical Coding & Robotics</CardSub>
                </div>
              </CardHeaderRow>
              <StatsGrid>
                <StatItem>
                  <StatValue>100%</StatValue>
                  <StatLabel>Hands-On Projects</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>Ages 5-16</StatValue>
                  <StatLabel>Tailored Groups</StatLabel>
                </StatItem>
              </StatsGrid>
            </FloatingCard>
          </RightIllustration>
        </Zoom>
      </ContentGrid>
    </CtaWrapper>
  );
}